import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, CheckCircle2, Lock, Share2, ShieldCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const FLASH_TYPE_LABELS = {
  actualidad: "Actualidad", confianza: "Confianza", prioridad: "Prioridad",
  reaccion: "Reacción", evaluacion: "Evaluación", decision: "Decisión", verificacion: "Verificación ciudadana",
};

function useCountdown(closesAt) {
  const [secs, setSecs] = useState(() => Math.max(0, Math.floor((new Date(closesAt) - new Date()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => setSecs(Math.max(0, Math.floor((new Date(closesAt) - new Date()) / 1000))), 1000);
    return () => clearInterval(t);
  }, [closesAt]);
  return secs;
}

function fmt(secs) {
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function FlashCard({ poll: initial, compact = false }) {
  const [poll, setPoll] = useState(initial);
  const [mainSel, setMainSel] = useState(null);
  const [subSel, setSubSel] = useState(null);
  const [step, setStep] = useState("main"); // main | sub
  const [busy, setBusy] = useState(false);
  const { user, login } = useAuth();
  const secs = useCountdown(poll.closes_at);
  const closed = poll.closed || secs <= 0;

  useEffect(() => { setPoll(initial); }, [initial]);

  const total = poll.total_votes;
  const results = poll.results || {};
  const subResults = poll.sub_results || {};
  const optTotal = Object.values(results).reduce((a, b) => a + b, 0) || 1;
  const subTotal = Object.values(subResults).reduce((a, b) => a + b, 0) || 1;

  const submit = async () => {
    if (!user) { toast.info("Crea tu cuenta verificada para participar."); login(); return; }
    if (!mainSel) return;
    setBusy(true);
    try {
      const r = await api.post(`/flash/${poll.id}/vote`, { option_id: mainSel, sub_option_id: subSel });
      setPoll(r.data);
      toast.success("Tu 2Voto fue registrado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar tu voto.");
    } finally { setBusy(false); }
  };

  const share = (network) => {
    const url = `${window.location.origin}/relampago/${poll.id}`;
    const text = `2Voto Relámpago: ${poll.question}`;
    const map = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    if (network === "copy") { navigator.clipboard.writeText(url); toast.success("Enlace copiado."); return; }
    window.open(map[network], "_blank", "noopener,noreferrer");
  };

  const voted = !!poll.user_vote;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`flash-card-${poll.id}`}>
      <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-cyan-400 text-xs font-bold tracking-[0.15em] uppercase">
          <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> 2Voto Relámpago
        </span>
        {closed ? (
          <span className="text-xs font-semibold text-slate-300">Cerrada</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-white font-mono text-sm font-semibold" data-testid="flash-countdown">
            <Clock className="w-4 h-4 text-cyan-400" /> {fmt(secs)}
          </span>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">{FLASH_TYPE_LABELS[poll.poll_type] || poll.poll_type}</span>
        </div>
        <h3 className="font-heading font-bold text-xl md:text-2xl text-slate-900 leading-snug">{poll.question}</h3>
        {poll.context && <p className="text-sm text-slate-600 mt-3">{poll.context}</p>}
        {poll.source && <p className="text-xs text-slate-400 mt-1">Fuente: {poll.source}</p>}

        {/* CLOSED: results */}
        {closed && poll.results && (
          <div className="mt-6" data-testid="flash-results">
            <div className="space-y-3">
              {poll.options.map((o) => {
                const c = results[o.id] || 0;
                const pct = Math.round((c / optTotal) * 100);
                return (
                  <div key={o.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-700 font-medium">{o.label}</span>
                      <span className="text-slate-500 font-semibold">{pct}%</span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {poll.subquestion && Object.keys(subResults).length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-800 mb-3">{poll.subquestion}</p>
                <div className="space-y-2">
                  {poll.sub_options.map((o) => {
                    const c = subResults[o.id] || 0;
                    const pct = Math.round((c / subTotal) * 100);
                    if (!c) return null;
                    return (
                      <div key={o.id} className="flex items-center gap-3 text-sm">
                        <span className="w-40 text-slate-600 shrink-0">{o.label}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-slate-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-slate-500 font-semibold w-10 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 mt-6 text-center">
              <div className="bg-slate-50 rounded-lg p-3"><p className="font-heading font-black text-lg text-slate-900">{total?.toLocaleString("es-PR")}</p><p className="text-[11px] text-slate-500">Participantes</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="font-heading font-black text-lg text-slate-900">{poll.verified_pct}%</p><p className="text-[11px] text-slate-500">Verificados</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="font-heading font-black text-lg text-rose-500">{poll.excluded}</p><p className="text-[11px] text-slate-500">Excluidos</p></div>
            </div>
            <p className="flex items-start gap-2 text-xs text-slate-400 mt-4"><ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> {poll.methodology}</p>
          </div>
        )}

        {/* ACTIVE: voting */}
        {!closed && !voted && (
          <div className="mt-6" data-testid="flash-vote">
            {step === "main" ? (
              <div className="space-y-2">
                {poll.options.map((o) => (
                  <button key={o.id} onClick={() => setMainSel(o.id)} data-testid={`flash-option-${o.id}`}
                    className={`w-full text-left rounded-lg border p-3.5 transition-all ${mainSel === o.id ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-cyan-300"}`}>
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      {mainSel === o.id && <CheckCircle2 className="w-4 h-4 text-cyan-600" />}{o.label}
                    </span>
                  </button>
                ))}
                <button onClick={() => { if (!mainSel) { toast.info("Selecciona una opción."); return; } poll.subquestion ? setStep("sub") : submit(); }}
                  disabled={busy} data-testid="flash-continue"
                  className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-rose-500 text-white font-semibold py-3 rounded-full hover:bg-rose-600 transition-colors disabled:opacity-60">
                  {poll.subquestion ? "Continuar" : "Emitir mi 2Voto"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-800 mb-2">{poll.subquestion}</p>
                {poll.sub_options.map((o) => (
                  <button key={o.id} onClick={() => setSubSel(o.id)} data-testid={`flash-sub-${o.id}`}
                    className={`w-full text-left rounded-lg border p-3 transition-all ${subSel === o.id ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-cyan-300"}`}>
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      {subSel === o.id && <CheckCircle2 className="w-4 h-4 text-cyan-600" />}{o.label}
                    </span>
                  </button>
                ))}
                <button onClick={submit} disabled={busy || !subSel} data-testid="flash-submit"
                  className="w-full mt-3 bg-rose-500 text-white font-semibold py-3 rounded-full hover:bg-rose-600 transition-colors disabled:opacity-60">
                  {busy ? "Registrando…" : "Emitir mi 2Voto"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ACTIVE: already voted (staged reveal) */}
        {!closed && voted && (
          <div className="mt-6 text-center" data-testid="flash-voted">
            <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-6 h-6 text-cyan-600" />
            </div>
            <p className="font-heading font-bold text-lg text-slate-900">Tu voto fue registrado</p>
            {poll.show_participation ? (
              <p className="text-sm text-slate-600 mt-2">{total?.toLocaleString("es-PR")} personas han participado. Los porcentajes se revelarán al cierre para evitar el efecto de arrastre.</p>
            ) : (
              <p className="flex items-center justify-center gap-1.5 text-sm text-slate-500 mt-2"><Lock className="w-4 h-4" /> Los resultados se revelarán cuando cierre la consulta.</p>
            )}
          </div>
        )}

        {/* Share */}
        <div className="flex items-center gap-2 mt-6 pt-5 border-t border-slate-100">
          <span className="text-xs text-slate-400 mr-1"><Share2 className="w-4 h-4 inline" /> Compartir:</span>
          <button onClick={() => share("whatsapp")} data-testid="flash-share-whatsapp" className="text-xs font-semibold text-slate-600 hover:text-cyan-600">WhatsApp</button>
          <button onClick={() => share("x")} className="text-xs font-semibold text-slate-600 hover:text-cyan-600">X</button>
          <button onClick={() => share("facebook")} className="text-xs font-semibold text-slate-600 hover:text-cyan-600">Facebook</button>
          <button onClick={() => share("copy")} className="text-xs font-semibold text-slate-600 hover:text-cyan-600">Copiar enlace</button>
        </div>
      </div>
    </div>
  );
}
